export type ApiQuality = 'standard' | 'premium';
export type ApiGenerationStatus = 'accepted' | 'succeeded' | 'failed' | 'blocked';
export type ApiModerationStatus = 'passed' | 'blocked' | 'needs_review';
export type ApiUserTier = 'anonymous' | 'logged_in' | 'credit_user' | 'subscriber' | 'admin';

export type ApiGenerateRequest = {
  prompt?: string;
  imageBase64?: string;
  imageMimeType?: 'image/png' | 'image/jpeg' | 'image/webp';
  stylePreset: string;
  outputCount: 1 | 2 | 4;
  quality: ApiQuality;
  consent: { hasUploadRights: boolean; acceptsContentPolicy: boolean };
  userTier?: ApiUserTier;
  requestId?: string;
};

export type ApiError = {
  code: string;
  message: string;
  safeForUser: true;
  retryable: boolean;
  details?: Record<string, string | number | boolean>;
};

export type ApiProviderResult = {
  provider: 'mock' | 'openai' | string;
  model: string;
  images: Array<{ id: string; url?: string; b64Json?: string; mimeType: string }>;
  usage: { inputImageTokens?: number; outputImageTokens?: number; estimatedCostUsd?: number; latencyMs: number };
};

export type ApiGenerationResponse = {
  requestId: string;
  status: ApiGenerationStatus;
  moderationStatus: ApiModerationStatus;
  quota: { allowed: boolean; userTier: ApiUserTier; remainingToday?: number; blockReason?: string };
  result?: ApiProviderResult;
  error?: ApiError;
};

export class GenerationApiError extends Error {
  status: number;
  code: string;
  retryable: boolean;
  response?: ApiGenerationResponse;

  constructor(status: number, code: string, message: string, retryable: boolean, response?: ApiGenerationResponse) {
    super(message);
    this.name = 'GenerationApiError';
    this.status = status;
    this.code = code;
    this.retryable = retryable;
    this.response = response;
  }
}

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');

export async function generateImage(payload: ApiGenerateRequest): Promise<ApiGenerationResponse> {
  const response = await fetch(`${apiBaseUrl}/generate`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null) as ApiGenerationResponse | { error?: ApiError } | null;
  if (!response.ok) {
    const generationResponse = isGenerationResponse(data) ? data : undefined;
    const apiError = generationResponse?.error ?? data?.error;
    throw new GenerationApiError(
      response.status,
      apiError?.code ?? `http_${response.status}`,
      apiError?.message ?? 'The generation API request failed. Please try again.',
      apiError?.retryable ?? response.status >= 500,
      generationResponse,
    );
  }

  if (!isGenerationResponse(data)) {
    throw new GenerationApiError(response.status, 'invalid_response', 'The generation API returned an unexpected response.', true);
  }

  return data;
}

export async function fileToBase64Payload(file: File): Promise<Pick<ApiGenerateRequest, 'imageBase64' | 'imageMimeType'>> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    throw new GenerationApiError(0, 'validation_error', 'Upload a JPG, PNG, or WebP image.', false);
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new GenerationApiError(0, 'file_read_error', 'The selected image could not be read.', true));
    reader.readAsDataURL(file);
  });

  return {
    imageBase64: dataUrl.replace(/^data:[^;]+;base64,/, ''),
    imageMimeType: file.type as ApiGenerateRequest['imageMimeType'],
  };
}

function isGenerationResponse(data: unknown): data is ApiGenerationResponse {
  if (!data || typeof data !== 'object') return false;
  const value = data as Partial<ApiGenerationResponse>;
  return typeof value.requestId === 'string' && typeof value.status === 'string' && typeof value.moderationStatus === 'string';
}
